import { z } from 'zod'

import {
  animalStatusSchema,
  breedSchema,
  exitReasonSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  labelSchema,
  moneyPaisa,
  noteSchema,
  nullableMoneyPaisa,
  ratioSchema,
  sexSchema,
  teethAgeSchema,
  versionSchema,
} from '../common'
import { mediaItemSchema } from '../media/media'

/**
 * A12–A21 · The animal.
 *
 * Everything here is a **response** shape, so every `...Paisa` key is optional
 * (§4.16) and every derived figure — cost basis, break-even, days held, daily
 * burn — appears here and **only** here. None of them exist on a create or
 * patch request: a client that could send a break-even could send a wrong one,
 * and the number this product exists for would stop being trustworthy.
 */

/** The facts a Staff account sees in full. No money, and nothing derived. */
export const animalFactsSchema = z.object({
  id: idSchema,
  tagNumber: z.string().nullish(),
  nickname: z.string().nullish(),
  sex: sexSchema,
  breed: breedSchema.nullish(),
  teethAtPurchase: teethAgeSchema,
  /**
   * Derived server-side from `teethAtPurchase` and denormalised for numeric
   * sorting ("youngest first"). Response-only: it is never accepted from a
   * client, or the two could disagree.
   */
  teethIncisors: z.number().int(),
  colour: z.string().nullish(),
  markings: z.string().nullish(),
  weightInKg: z.number().positive().nullish(),
  weightOutKg: z.number().positive().nullish(),
  /** How much of the herd's feed this animal eats relative to one head. */
  consumptionUnit: z.number().positive(),
  status: animalStatusSchema,
  purchaseDate: isoDateSchema,
  purchaseMandi: z.string().nullish(),
  sellerPartyId: idSchema.nullish(),
  sellerName: z.string().nullish(),
  lotId: idSchema.nullish(),
  lotName: z.string().nullish(),
  seasonTag: z.string().nullish(),
  notes: noteSchema.nullish(),
  exitDate: isoDateSchema.nullish(),
  exitReason: exitReasonSchema.nullish(),
  deathCause: z.string().nullish(),
})

/**
 * §4.6 · What it has cost, broken into the five components that sum exactly to
 * `totalPaisa`. The whole object is absent for an account that may not see
 * money; the individual keys are optional too, because the Staff same-day
 * expense rule (§4.16) can make one figure visible while its neighbours are not.
 *
 * `feedEstimatePaisa` and `feedActualPaisa` are **two separate lines on
 * purpose** (BR §6). The estimate is the daily rate standing in for a bill that
 * has not arrived; the actual is the real wanda bill allocated by animal-days.
 * When the bill lands, the estimate is suppressed for the days it covers and
 * the money moves from one line to the other. The trader has to be able to
 * watch that happen, or the number simply changes under him and he stops
 * believing it. `feedSuppressedDays` / `feedBillableDays` are the same story
 * in days.
 */
export const costBreakdownSchema = z.object({
  purchasePricePaisa: moneyPaisa(),
  purchaseSideCostsPaisa: moneyPaisa(),
  directHoldingCostsPaisa: moneyPaisa(),
  sharedAllocatedPaisa: moneyPaisa(),
  feedEstimatePaisa: moneyPaisa(),
  feedActualPaisa: moneyPaisa(),
  totalPaisa: moneyPaisa(),
  daysHeld: z.number().int().nonnegative(),
  dailyBurnPaisa: moneyPaisa(),
  feedSuppressedDays: z.number().int().nonnegative().optional(),
  feedBillableDays: z.number().int().nonnegative().optional(),
  /**
   * The same figures as the screen says them: "Share of farm rent · 90 days of
   * Rs 18,000/month". The component fields above are the arithmetic; these are
   * the sentences. Sent by the server because the sub-line is a *derivation*
   * ("90 days of…"), and a client that assembled it would be doing costing.
   */
  lines: z.array(
    z.object({
      key: z.string(),
      label: labelSchema,
      sub: z.string().nullish(),
      /** Present for a lot-split or recurring line, so the row can link out. */
      sourceId: idSchema.nullish(),
      amountPaisa: moneyPaisa(),
    }),
  ),
})

/** One entry in the weight log (A20, U7). Weights are not money — never masked. */
export const weightEntrySchema = z.object({
  id: idSchema,
  animalId: idSchema,
  weighedOn: isoDateSchema,
  weightKg: z.number().positive(),
  note: noteSchema.nullish(),
  createdAt: isoInstantSchema,
})

/**
 * A13 · A row in the animals list. Deliberately smaller than the detail: the
 * list is the screen a trader scrolls at a mandi on one bar of signal.
 */
export const animalSummarySchema = animalFactsSchema
  .pick({
    id: true,
    tagNumber: true,
    nickname: true,
    sex: true,
    breed: true,
    teethAtPurchase: true,
    teethIncisors: true,
    colour: true,
    status: true,
    purchaseDate: true,
    lotId: true,
  })
  .extend({
    daysHeld: z.number().int().nonnegative(),
    coverMediaId: idSchema.nullish(),
    purchasePricePaisa: moneyPaisa(),
    costBasisPaisa: moneyPaisa(),
    breakEvenPaisa: moneyPaisa(),
    askingPricePaisa: moneyPaisa(),
    dailyBurnPaisa: moneyPaisa(),
    /** Sold rows only, and the default sort of every sold list (§4.10). */
    salePricePaisa: moneyPaisa(),
    profitPaisa: moneyPaisa(),
    profitPerDayPaisa: moneyPaisa(),
    version: versionSchema,
  })

/** The sale, as the animal's own page shows it. Full detail is `GET /sales/:id`. */
export const animalSaleSummarySchema = z.object({
  id: idSchema,
  saleDate: isoDateSchema,
  buyerPartyId: idSchema.nullish(),
  buyerName: z.string().nullish(),
  daysHeld: z.number().int().nonnegative(),
  commissionBps: z.number().int(),
  salePricePaisa: moneyPaisa(),
  commissionPaisa: moneyPaisa(),
  costBasisPaisa: moneyPaisa(),
  profitPaisa: moneyPaisa(),
  profitPerDayPaisa: moneyPaisa(),
  outstandingPaisa: moneyPaisa(),
  /** Days the balance has been outstanding — drives the ageing chip. */
  outstandingDays: z.number().int().nonnegative().optional(),
  needsReview: z.boolean(),
})

/**
 * A14 · `GET /animals/:id` — the most-read payload in the product.
 *
 * The facts are always here. Everything money-shaped is optional, and under
 * Price Lock or a Staff session the keys are simply gone.
 */
export const animalDetailResponse = animalFactsSchema.extend({
  daysHeld: z.number().int().nonnegative(),
  /** The server's `as_of` for every derived figure below. Never the client's. */
  asOf: isoDateSchema,
  askingPricePaisa: moneyPaisa(),
  costBreakdown: costBreakdownSchema.optional(),
  breakEvenPaisa: moneyPaisa(),
  dailyBurnPaisa: moneyPaisa(),
  /**
   * §4.12 · `asking − expectedSaleCosts − costBasis`. **`null` when no asking
   * price is set** — the app never invents a market value — which is why this
   * one is nullable *and* optional: null means "not priced", absent means "not
   * your figure to see".
   */
  unrealisedPaisa: nullableMoneyPaisa(),
  /** §4.11 · Set on a `DIED`/`LOST` animal: `salvage − costBasis`, a realised loss. */
  salvageValuePaisa: moneyPaisa(),
  exitLossPaisa: moneyPaisa(),
  media: z.array(mediaItemSchema),
  weights: z.array(weightEntrySchema),
  sale: animalSaleSummarySchema.nullish(),
  ownershipShares: z
    .array(
      z.object({
        partyId: idSchema.nullish(),
        partyName: z.string().nullish(),
        shareBps: z.number().int().min(0).max(10_000),
        capitalContributedPaisa: moneyPaisa(),
      }),
    )
    .optional(),
  version: versionSchema,
  createdAt: isoInstantSchema,
  updatedAt: isoInstantSchema,
})

/** Days-held is a fact; return on capital is not. Kept beside each other. */
export const animalReturnSchema = z.object({
  roiPercent: ratioSchema.optional(),
  annualisedRoiPercent: ratioSchema.optional(),
})

export type AnimalFacts = z.infer<typeof animalFactsSchema>
export type CostBreakdown = z.infer<typeof costBreakdownSchema>
export type AnimalSummary = z.infer<typeof animalSummarySchema>
export type AnimalDetail = z.infer<typeof animalDetailResponse>
export type AnimalSaleSummary = z.infer<typeof animalSaleSummarySchema>
export type WeightEntry = z.infer<typeof weightEntrySchema>
